import { PrivateRoute } from "@crispico/foundation-react";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { SplitPaneExt } from "@crispico/foundation-react/components/ReactSplitPaneExt/ReactSplitPaneExt";
import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { flightEntityDescriptor } from "AppEntityDescriptors";
import { HumanResourceScheduleEntityDescriptor } from "pages/HumanResourceSchedule/HumanResourceScheduleEntityDescriptor";
import React from "react";
import { RealTimeHumanResourceScheduleEmbeddedTable, RealTimeHumanResourceScheduleEmbeddedTableRRC } from "../components/tables/RealTimeHumanResourceScheduleEmbeddedTable";
import { RealTimeFlightEmbeddedTable, RealTimeFlightEmbeddedTableRRC } from "../components/tables/RealTimeFlightEmbeddedTable";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { AppContainerContext } from "@crispico/foundation-react/AppContainerContext";
import { Utils } from "@crispico/foundation-react/utils/Utils";
import { Button, Checkbox, Form, FormField, Header, Icon, Segment, SemanticICONS } from "semantic-ui-react";
import { DatePickerReactCalendar } from "@crispico/foundation-react/components/DatePicker/DatePickerReactCalendar/DatePickerReactCalendar";
import moment from "moment-timezone";
import { Mission, Task } from "types";
import { DragAndDropControllerNew } from "../DragAndDropControllerNew";
import { DragAndDropArea } from "../DragAndDrop";
import { ScriptableContainer } from "@famiprog-foundation/scriptable-ui";
import { GANTT_HUMAN_RESOURCE_SCHEDULE_SHOW_PAGE } from "app";

export const RT_FLIGHT_HRS_OPTIONS_LOCAL_STORAGE_ITEM = "RealTimeFlightHumanResourceSchedulePage.options";
interface RealTimeFlightHumanResourceSchedulePageOptions {
    date: string | null
}
class RealTimeFlightHumanResourceSchedulePageState extends State {
    autoRefreshEnabled: boolean = true;
    options: RealTimeFlightHumanResourceSchedulePageOptions = (() => {
        const defaultOptions: RealTimeFlightHumanResourceSchedulePageOptions = { date: Utils.now().toISOString() };
        try {
            const raw = window.localStorage.getItem(RT_FLIGHT_HRS_OPTIONS_LOCAL_STORAGE_ITEM);
            if (raw) {
                const lsOptions = JSON.parse(raw);
                return { ...defaultOptions, ...lsOptions };
            }
        } catch (e) {
        }
        return defaultOptions;
    })();
}
class RealTimeFlightHumanResourceSchedulePageReducers<S extends RealTimeFlightHumanResourceSchedulePageState = RealTimeFlightHumanResourceSchedulePageState> extends Reducers<S> {

}

type RealTimeFlightHumanResourceSchedulePageProps = RRCProps<RealTimeFlightHumanResourceSchedulePageState, RealTimeFlightHumanResourceSchedulePageReducers>;

export class RealTimeFlightHumanResourceSchedulePage extends React.Component<RealTimeFlightHumanResourceSchedulePageProps> {

    protected ganttHumanResourceSchedulesTableRef = ReduxReusableComponents.createRef<RealTimeHumanResourceScheduleEmbeddedTable>();
    protected ganttFlightTableRef = ReduxReusableComponents.createRef<RealTimeFlightEmbeddedTable>();

    constructor(props: RealTimeFlightHumanResourceSchedulePageProps) {
        super(props);

        this.getFullEntity = this.getFullEntity.bind(this);
    }

    componentDidMount() {
        this.componentDidUpdateInternal();
        AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground = AppMetaTempGlobals.history.location;
    }

    componentWillUnmount(): void {
        AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground = undefined;
        this.stopTimer();
    }

    componentDidUpdate(prevProps: RealTimeFlightHumanResourceSchedulePageProps) {
        this.componentDidUpdateInternal(prevProps);
    }

    protected isDateTodayOrFutureDay(dateStr: string | null): boolean {
        if (!dateStr) return false;
        const today = moment(Utils.now()).startOf('day');
        const selectedDate = moment(dateStr).startOf('day');
        return selectedDate.isSameOrAfter(today, 'day');
    }

    protected async componentDidUpdateInternal(prevProps?: RealTimeFlightHumanResourceSchedulePageProps) {
        if (!prevProps) {
            this.props.r.setInReduxState({ autoRefreshEnabled: this.isDateTodayOrFutureDay(this.props.s.options.date) });
        }
        if (prevProps && prevProps.s.options.date !== this.props.s.options.date) {
            await this.props.r.setInReduxState({ autoRefreshEnabled: this.isDateTodayOrFutureDay(this.props.s.options.date) });
            this.refreshAll(true);
        }
    }

    protected async refreshAll(changeDate: boolean) {
        console.log('🔄 refreshAll() called - stopping timer, refreshing tables, restarting timer');
        this.stopTimer();
        if (changeDate) {
            await Promise.all([
                this.ganttHumanResourceSchedulesTableRef.currentNotNull.setDate(this.props.s.options.date),
                this.ganttFlightTableRef.currentNotNull.setDate(this.props.s.options.date),
            ]);
        }
        await Promise.all([
            this.ganttHumanResourceSchedulesTableRef.currentNotNull.superRefresh(),
            this.ganttFlightTableRef.currentNotNull.superRefresh(),
        ]);

        this.startTimer();
    }

    private timer: number | undefined = undefined;
    private static REFRESH_RATE: number = 5000; // 5s

    private startTimer() {
        if (!this.props.s.autoRefreshEnabled) {
            return;
        }
        console.log('🔄 Starting timer for real-time updates');
        this.timer = window.setTimeout(async () => {
            console.log('⏰ Timer triggered - calling refreshInternal()');
            await this.refreshInternal();
            this.startTimer();
        }, RealTimeFlightHumanResourceSchedulePage.REFRESH_RATE);
    }

    private stopTimer() {
        console.log('🛑 Stopping timer');
        clearTimeout(this.timer);
        this.timer = undefined;
    }

    protected async refreshInternal() {
        console.log('🔄 refreshInternal() called - refreshing tables in parallel');
        await Promise.all([
            this.ganttHumanResourceSchedulesTableRef.currentNotNull.refreshInternal(),
            this.ganttFlightTableRef.currentNotNull.refreshInternal(),
        ]);
    }

    getFullEntity(entityName: string, entity: any) {
        if (entityName === "Task" && !Utils.isNullOrEmpty((entity as Task).mission)) {
            // populate the mission field from HRS gantt
            return { ...entity, mission: this.ganttHumanResourceSchedulesTableRef.currentNotNull.getMission((entity as Task).mission.id) };
        }
        if (entityName === "Mission") {
            // populate the tasks field from Flight gantt
            return { ...entity, tasks: this.ganttFlightTableRef.currentNotNull.getTasks((entity as Mission).id) };
        }
        return entity;
    }

    renderTopBar() {
        return <Segment className="flex-container-row flex-wrap flex-center less-margin-top-bottom gap10">
            <Header as="h3" className="small-margin-top no-margin">
                <Icon name={realTimeFlightHumanResourceSchedulePageMenuEntry().icon as SemanticICONS} />{realTimeFlightHumanResourceSchedulePageMenuEntry().content}</Header>
            <Form>
                <FormField>
                    <DatePickerReactCalendar key={`date-picker`}
                        format={Utils.dateFormat} fieldName="date" allowClear={false} value={moment(this.props.s.options.date)}
                        onChange={async (value) => {
                            let newDate = value ? value.toISOString() : Utils.now().toISOString();
                            const newOptions = { ...this.props.s.options, date: newDate };
                            await this.props.r.setInReduxState({ options: newOptions });
                            window.localStorage.setItem(RT_FLIGHT_HRS_OPTIONS_LOCAL_STORAGE_ITEM, JSON.stringify(newOptions));
                        }} />
                </FormField>
            </Form>
            <Button data-testid="RefreshButton_refresh" key="refresh" color="green" icon="refresh" onClick={async () => {
                console.log('🔘 Manual refresh button clicked');
                this.refreshAll(true);
            }} />
            <FormField className="small-margin-left small-margin-right">
                <Checkbox toggle label={_msg("RefreshButton.modal.header")} checked={this.props.s.autoRefreshEnabled}
                    onChange={async () => {
                        await this.props.r.setInReduxState({ autoRefreshEnabled: !this.props.s.autoRefreshEnabled });
                        if (this.props.s.autoRefreshEnabled) {
                            this.startTimer();
                        } else {
                            this.stopTimer();
                        }
                    }}
                />
            </FormField>
        </Segment>
    }

    render() {
        return <AppContainerContext.Consumer>
            {context => <>
                <Utils.Observer value={context.initializationsForClient.currentOrganizationDropdownValue} didUpdate={() => {
                    console.log('🏢 Organization changed - triggering refreshAll()');
                    this.refreshAll(true);
                }} />
                <div className="flex-container flex-grow">
                    {this.renderTopBar()}
                    <DragAndDropArea renderLayer={DragAndDropControllerNew.INSTANCE.renderDragLayer}>
                        <SplitPaneExt size={"50%"} className="RealTimeFlightHumanResourceSchedulePage">
                            <ScriptableContainer id={"RealTimeFlightHumanResourceSchedulePage_FlightTable"}>
                            <RealTimeFlightEmbeddedTableRRC ref={this.ganttFlightTableRef} id={`entityTablePage-${flightEntityDescriptor.name}`}
                                currentLocation={AppMetaTempGlobals.history.location}
                                entityDescriptor={flightEntityDescriptor}
                                onRefresh={() => this.refreshAll(false)} getFullEntity={this.getFullEntity} />
                            </ScriptableContainer>
                            <ScriptableContainer id={"RealTimeFlightHumanResourceSchedulePage_HumanResourceScheduleTable"}>
                                <RealTimeHumanResourceScheduleEmbeddedTableRRC ref={this.ganttHumanResourceSchedulesTableRef} id={`entityTablePage-${HumanResourceScheduleEntityDescriptor.name}`}
                                    currentLocation={AppMetaTempGlobals.history.location}
                                    entityDescriptor={entityDescriptors["HumanResourceSchedule"]}
                                    onRefresh={() => this.refreshAll(false)} getFullEntity={this.getFullEntity} />
                            </ScriptableContainer>
                        </SplitPaneExt>
                    </DragAndDropArea>
                </div>
            </>}
        </AppContainerContext.Consumer>
    }
}

export const realTimeFlightHumanResourceSchedulePageUrl = "/RealTimeFlightHumanResourceSchedulePage";
export const realTimeFlightHumanResourceSchedulePageUrlRoute = () =>
    <PrivateRoute key="RealTimeFlightHumanResourceSchedulePage"
        path={realTimeFlightHumanResourceSchedulePageUrl}
        permission={GANTT_HUMAN_RESOURCE_SCHEDULE_SHOW_PAGE}
        render={(props) => { return <RealTimeFlightHumanResourceSchedulePageRRC {...props} id="RealTimeFlightHumanResourceSchedulePage" /> }}
    />

export const realTimeFlightHumanResourceSchedulePageMenuEntry = () => {
    return {
        id: "RealTimeFlightHumanResourceSchedulePage",
        content: _msg("RealTimeFlightHumanResourceSchedulePage.label"),
        to: realTimeFlightHumanResourceSchedulePageUrl, exact: true, icon: "chart bar outline",
        permission: GANTT_HUMAN_RESOURCE_SCHEDULE_SHOW_PAGE
    }
};

export const RealTimeFlightHumanResourceSchedulePageRRC = ReduxReusableComponents.connectRRC(RealTimeFlightHumanResourceSchedulePageState, RealTimeFlightHumanResourceSchedulePageReducers, RealTimeFlightHumanResourceSchedulePage);
